import React from 'react';
import {
  Badge,
  Box,
  Button,
  HStack,
  IconButton,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { FiChevronLeft, FiChevronRight, FiEye, FiEdit2 } from 'react-icons/fi';
import { SaktiPaginationMeta, SaktiBkmListItem } from '../types';
import { SAKTI_UI_SIZE } from '../uiSizes';

interface SaktiBkmTableProps {
  loading: boolean;
  rows: SaktiBkmListItem[];
  pagination: SaktiPaginationMeta;
  onPageChange: (page: number) => void;
  onViewDetail: (id: string) => void;
  onEdit: (id: string) => void;
}

const SaktiBkmTable: React.FC<SaktiBkmTableProps> = ({
  loading,
  rows,
  pagination,
  onPageChange,
  onViewDetail,
  onEdit,
}) => {
  return (
    <VStack align="stretch" spacing={4}>
      <Skeleton isLoaded={!loading} borderRadius={SAKTI_UI_SIZE.cardRadius}>
        <Box borderRadius={SAKTI_UI_SIZE.cardRadius} overflow="hidden" borderWidth="1px" borderColor="gray.200" bg="white">
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead bg="customGreen.500">
                <Tr>
                  <Th color="white">No. BKM</Th>
                  <Th color="white">Estate / Divisi</Th>
                  <Th color="white">Pemanen</Th>
                  <Th color="white" isNumeric>HA</Th>
                  <Th color="white" isNumeric>Hanca</Th>
                  <Th color="white" isNumeric>P-01</Th>
                  <Th color="white" isNumeric>P-02</Th>
                  <Th color="white" isNumeric>P-03</Th>
                  <Th color="white">Status</Th>
                  <Th color="white">Aksi</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.length === 0 ? (
                  <Tr><Td colSpan={10}><Text color="gray.500" textAlign="center" py={4}>Tidak ada data</Text></Td></Tr>
                ) : (
                  rows.map((b) => (
                    <Tr key={b.id}>
                      <Td>
                        <Text fontWeight="bold">{b.bkm_nbr}</Text>
                        <Text fontSize="xs" color="gray.500">{b.bkm_date}</Text>
                      </Td>
                      <Td><Text>{b.estate_name}</Text><Text fontSize="xs" color="gray.500">{b.division_name}</Text></Td>
                      <Td>
                        <Text fontWeight="bold">{b.harvester_name}</Text>
                        <Text fontSize="xs" color="gray.500">{b.harvester_nip}</Text>
                      </Td>
                      <Td isNumeric><strong>{b.ha_amt.toFixed(2)}</strong></Td>
                      <Td isNumeric>{b.hanca_count}</Td>
                      <Td isNumeric color="gray.500">{b.p01.toFixed(2)}</Td>
                      <Td isNumeric color="gray.500">{b.p02.toFixed(2)}</Td>
                      <Td isNumeric color="gray.500">{b.p03.toFixed(2)}</Td>
                      <Td>
                        <Badge colorScheme={b.status === 'ACTIVE' ? 'blue' : 'gray'}>
                          {b.status}
                        </Badge>
                      </Td>
                      <Td>
                        <HStack spacing={1}>
                          <IconButton size="xs" icon={<FiEye />} aria-label="View" onClick={() => onViewDetail(b.id)} />
                          <IconButton size="xs" icon={<FiEdit2 />} aria-label="Edit" onClick={() => onEdit(b.id)} />
                        </HStack>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </Box>
          <HStack px={4} py={3} bg="customGreen.500" color="white" justify="space-between">
            <Text fontSize="xs" fontWeight="semibold">TOTAL {pagination.total_items.toLocaleString('id-ID')} RECORDS</Text>
            <HStack spacing={1}>
              <IconButton aria-label="Prev" icon={<FiChevronLeft />} size="sm" variant="outline" isDisabled={pagination.page <= 1} onClick={() => onPageChange(pagination.page - 1)} />
              <Button size="sm" variant="solid" bg="white" color="customGreen.500">{pagination.page}</Button>
              <IconButton aria-label="Next" icon={<FiChevronRight />} size="sm" variant="outline" isDisabled={pagination.page >= pagination.total_pages} onClick={() => onPageChange(pagination.page + 1)} />
            </HStack>
          </HStack>
        </Box>
      </Skeleton>
    </VStack>
  );
};

export default SaktiBkmTable;
